import EventFilterActions from 'actions/EventFilterActions';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { CommonContext } from 'components/Contexts';
import {
  TIME_TARGETS,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import React, { useContext } from 'react';
import { useEventReports } from 'src/hooks/useEventReports';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import type ReportUtil from 'utils/ReportUtil';
import { getValidationErrorMessage } from 'utils/yupUtils';
import SpotCheckFormFields, { getReportSelectMessages } from '../../../Aspen/SpotCheckFormFields';
import SpotCheckScheduleFields from '../../../Aspen/SpotCheckScheduleFields';

interface SpotCheckFormProps {
  spotCheck?: ScheduledActivityEntity;
  spotCheckForm: UseSpotCheckForm;
  timezoneId: string;
  formMode: 'create' | 'edit';
  selectedReportId: string[];
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  eventsMonthlyQuotaLimit: number | undefined;
}

export default function SpotCheckForm(props: SpotCheckFormProps) {
  const {
    spotCheckForm,
    timezoneId,
    formMode,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    eventsMonthlyQuotaLimit,
  } = props;

  const {
    setAiChatAnswerFormat,
    aiChatAnswerFormat,
    spotCheckValidationError,
    setSelectedEventReport,
    selectedEventReport,
    setEventTimestampOffset,
    setEventTimeTarget,
  } = spotCheckForm;

  const locale = useLocale();

  const [isScheduledDetailsVisible, setIsScheduledDetailsVisible] = React.useState(false);
  const [isResponseFormatCollapsed, setIsResponseFormatCollapsed] = React.useState(true);

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);

  const isLivePreview = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';
  const disableEditExpirationType = formMode === 'edit';
  const disableStartTimeCheck = formMode === 'edit';

  const eventReports = useEventReports();

  React.useEffect(() => {
    if (!eventReports?.length) {
      EventFilterActions.fetchFilters();
    }
  }, [eventReports?.length]);

  const reportOptions =
    eventReports?.map((report: ReportUtil) => {
      return {
        value: report.templateId,
        label: report.getName(locale.localeCode),
      };
    }) || [];

  const [selectedReportOption, setSelectedReportOption] = React.useState<string | undefined>();

  const {
    authData: { firstDayOfWeek },
  } = useContext(CommonContext);

  const startTimeErrorText = getValidationErrorMessage(spotCheckValidationError, 'startTime');

  const handleSelectedReportOption = (e: { value: string }) => {
    setSelectedReportOption(e.value);
    const specReport = eventReports?.find((report) => report.templateId === e.value);
    setSelectedEventReport(specReport);
    setEventTimestampOffset(0);
    setEventTimeTarget(TIME_TARGETS.START);
  };

  React.useEffect(() => {
    if (selectedEventReport?.templateId) {
      setSelectedReportOption(selectedEventReport?.templateId);
    }
  }, [selectedEventReport?.templateId]);

  const { reportSelectErrorText, reportSelectWarningText } = getReportSelectMessages(
    locale,
    isEventSpotCheck,
    selectedEventReport,
    isEventsEstimateBelowLimit,
    totalEventsInLast30Days,
    eventsMonthlyQuotaLimit,
  );

  const scheduleStepContent = !isEventSpotCheck ? (
    <SpotCheckScheduleFields
      spotCheckForm={spotCheckForm}
      timezoneId={timezoneId}
      calendarStartDay={firstDayOfWeek}
      isLivePreview={isLivePreview}
      isScheduledDetailsVisible={isScheduledDetailsVisible}
      setIsScheduledDetailsVisible={setIsScheduledDetailsVisible}
      startTimeErrorText={startTimeErrorText}
      disableStartTimeCheck={disableStartTimeCheck}
      disableEditExpirationType={disableEditExpirationType}
    />
  ) : undefined;

  return (
    <form className="tw flex min-h-0 flex-1 flex-col justify-start">
      <SpotCheckFormFields
        spotCheckForm={spotCheckForm}
        reportOptions={reportOptions}
        selectedReportOption={selectedReportOption}
        onReportChange={(value) => handleSelectedReportOption({ value })}
        reportSelectErrorText={reportSelectErrorText}
        reportSelectWarningText={reportSelectWarningText}
        isEventSpotCheck={isEventSpotCheck}
        isResponseFormatCollapsed={isResponseFormatCollapsed}
        setIsResponseFormatCollapsed={setIsResponseFormatCollapsed}
        aiChatAnswerFormat={aiChatAnswerFormat}
        setAiChatAnswerFormat={setAiChatAnswerFormat}
        scheduleStep={scheduleStepContent}
      />
    </form>
  );
}
